import React, { useState, useMemo } from "react"
import { SnapshotSummaryData } from "~/api/snapshot_summaries"
import { formatCurrency, formatCurrencyFull, formatTvpi } from "./utils"
import Tooltip from "~/components/Tooltip"

interface PortcoRow {
  name: string
  series: string
  sector: string | null
  cost: number
  fair_value: number
  distribution: number
  dist_itd: number
  tvpi: number | null
  pl: number
}

type SortCol =
  | "name"
  | "series"
  | "sector"
  | "cost"
  | "fair_value"
  | "distribution"
  | "dist_itd"
  | "tvpi"
  | "pl"

function tvpiClass(tvpi: number | null | undefined): string {
  if (tvpi == null) return ""
  return tvpi >= 1.0 ? "b-cell--positive" : "b-cell--negative"
}

function valueClass(value: number | null | undefined): string {
  if (value == null) return ""
  if (value > 0) return "b-cell--positive"
  if (value < 0) return "b-cell--negative"
  return ""
}

function cTd(value: number | null | undefined, extraClass?: string) {
  return (
    <td className={extraClass}>
      <Tooltip tip={value != null ? formatCurrencyFull(value) : undefined}>
        {formatCurrency(value)}
      </Tooltip>
    </td>
  )
}

function buildPortcoRows(data: SnapshotSummaryData): PortcoRow[] {
  return data.series.flatMap((series) =>
    (series.assets ?? [])
      .filter((a) => !a.is_other)
      .map((a) => ({
        name: a.name,
        series: series.series,
        sector: a.sector,
        cost: a.cost,
        fair_value: a.fair_value,
        distribution: a.distribution,
        dist_itd: a.dist_itd,
        tvpi: a.tvpi,
        pl: a.pl,
      })),
  )
}

function getColValue(row: PortcoRow, col: SortCol): string | number | null {
  switch (col) {
    case "name":
      return row.name
    case "series":
      return row.series
    case "sector":
      return row.sector
    case "cost":
      return row.cost
    case "fair_value":
      return row.fair_value
    case "distribution":
      return row.distribution
    case "dist_itd":
      return row.dist_itd
    case "tvpi":
      return row.tvpi
    case "pl":
      return row.pl
  }
}

interface PortfolioCompanyTableProps {
  data: SnapshotSummaryData
  filterSectors: string[]
  filterText?: string
}

const PortfolioCompanyTable = ({
  data,
  filterSectors,
  filterText = "",
}: PortfolioCompanyTableProps) => {
  const [sortCol, setSortCol] = useState<SortCol | null>(null)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortCol(col)
      setSortDir("desc")
    }
  }

  const allRows = useMemo(() => buildPortcoRows(data), [data])

  const filterTextLower = filterText.toLowerCase()

  const filteredRows = useMemo(() => {
    return allRows.filter((r) => {
      if (filterTextLower && !r.name.toLowerCase().includes(filterTextLower))
        return false
      if (filterSectors.length > 0 && !filterSectors.includes(r.sector ?? ""))
        return false
      return true
    })
  }, [allRows, filterTextLower, filterSectors])

  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows
    return [...filteredRows].sort((a, b) => {
      const av = getColValue(a, sortCol)
      const bv = getColValue(b, sortCol)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp =
        typeof av === "string"
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number)
      return sortDir === "asc" ? cmp : -cmp
    })
  }, [filteredRows, sortCol, sortDir])

  const th = (col: SortCol, label: string, tooltip?: string) => (
    <th
      key={col}
      onClick={() => handleSort(col)}
      className={sortCol === col ? "sort-active" : ""}
    >
      <Tooltip tip={tooltip} asChild>
        <span className="b-th-inner">
          <span className="b-th-label">{label}</span>
          <span className="b-sort-indicator">
            {sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
          </span>
        </span>
      </Tooltip>
    </th>
  )

  if (allRows.length === 0) {
    return (
      <div className="b-snapshot-summary-no-data">
        <p className="b-snapshot-summary-no-data-message">
          No portfolio company data available.
        </p>
        <p className="b-snapshot-summary-no-data-hint">
          Portfolio company data appears when series have finalized snapshots
          with company-level holdings.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="b-snapshot-summary-table-wrapper">
        <table className="b-snapshot-summary-table b-snapshot-summary-table--portco">
          <thead>
            <tr>
              {th("name", "Company")}
              {th("series", "Series")}
              {th("sector", "Sector")}
              {th(
                "cost",
                "Cost",
                "Estimated LP cost basis: gross fund cost from financial statement × LP ownership share",
              )}
              {th(
                "fair_value",
                "Fair Value",
                "Estimated LP fair value: gross fund fair value from financial statement × LP ownership share",
              )}
              {th(
                "distribution",
                "Dist (QTD)",
                "Net distributions this quarter",
              )}
              {th(
                "dist_itd",
                "Dist (ITD)",
                "Net distributions since inception",
              )}
              {th(
                "tvpi",
                "TVPI",
                "Estimated TVPI: (Fair Value + Net Dist ITD) ÷ Cost. Uses gross-statement-derived values scaled to LP share.",
              )}
              {th("pl", "P&L", "Fair Value + Dist ITD − Cost")}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td className="b-snapshot-summary-empty" colSpan={9}>
                  No results match the active filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row, i) => (
                <tr
                  key={`${row.series}-${row.name}-${i}`}
                  className="b-snapshot-summary-row"
                >
                  <td className="b-snapshot-summary-label-cell">{row.name}</td>
                  <td>{row.series}</td>
                  <td>{row.sector || "—"}</td>
                  {cTd(row.cost)}
                  {cTd(row.fair_value)}
                  {cTd(row.distribution)}
                  {cTd(row.dist_itd)}
                  <td className={tvpiClass(row.tvpi)}>
                    {formatTvpi(row.tvpi)}
                  </td>
                  <td className={valueClass(row.pl)}>
                    {formatCurrency(row.pl)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="b-snapshot-summary-portco-footnote">
        Cost and Fair Value are derived from fund financial statements and
        scaled to the LP&rsquo;s estimated ownership share. They are
        approximations, not directly reported LP-level figures.
      </p>
    </div>
  )
}

export default PortfolioCompanyTable
